/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import { Drawer } from 'app/components/organisms/drawer/drawer';
import { Input } from 'app/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select';
import {
  useCreatePoolInvestFromBank,
  usePoolInvestBankLinks,
  usePoolInvestProject,
  usePoolInvestProjects,
  useRemovePoolInvestBankLinks
} from 'app/hooks/requests/pool-invest/use-pool-invest';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import type { Transaction } from 'app/types/transaction';
import BigNumber from 'bignumber.js';
import { PiggyBank, Trash2 } from 'lucide-react';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * `Props` type.
 */

type Props = {
  item: Transaction;
  refetch: () => void;
};

/**
 * Format a money value for the "allocated to" list.
 */

function fmtMoney(value: string | null): string {
  return new BigNumber(value ?? '0').decimalPlaces(2).toFormat(2);
}

/**
 * Export `PoolAllocationAction` — feature-guarded entry point. Only mounts the
 * (query-heavy) inner action when the `poolInvest` feature is enabled, so other
 * users never fire pool requests from the bank movements ledger.
 */

export function PoolAllocationAction(props: Props): JSX.Element | null {
  const enabled = useFeature('poolInvest');

  return enabled ? <PoolAllocationActionInner {...props} /> : null;
}

/**
 * Allocates a bank transaction into an investment pool — trace-only, so the bank
 * movement stays in bank accounting. The €200 transfer can be split into the
 * Férias value (`amount`) plus an ancillary cost (`fee` + its description, e.g.
 * the €4 conversion fee) without altering the bank record.
 */

function PoolAllocationActionInner({ item, refetch }: Props): JSX.Element {
  const { data: projects } = usePoolInvestProjects();
  const { data: bankLinks } = usePoolInvestBankLinks();
  const create = useCreatePoolInvestFromBank();
  const removeLinks = useRemovePoolInvestBankLinks();

  const [isOpen, setOpen] = useState(false);
  const [projectId, setProjectId] = useState<string>('');
  const [bucketId, setBucketId] = useState<string>('');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [feeNote, setFeeNote] = useState('');
  const [date, setDate] = useState<moment.Moment>(moment());

  const { data: project } = usePoolInvestProject(projectId || undefined);
  const categoryOptions = useMemo(() => project?.buckets ?? [], [project]);

  const ownedProjects = useMemo(() => (projects ?? []).filter(p => p.isOwner), [projects]);
  const links = useMemo(
    () => (bankLinks ?? []).filter(link => String(link.sourceEntryId) === String(item.id)),
    [bankLinks, item.id]
  );
  const linked = links.length > 0;

  const handleOpen = useCallback(
    (event?: React.MouseEvent<HTMLElement>) => {
      event?.stopPropagation();

      const absAmount = new BigNumber(item.amount).abs();

      setProjectId('');
      setBucketId('');
      setDirection('in');
      setAmount(absAmount.gt(0) ? absAmount.decimalPlaces(2).toString() : '');
      setFee('');
      setFeeNote('');
      setDate(moment(item.date));
      setOpen(true);
    },
    [item.amount, item.date]
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const handleApply = useCallback(async () => {
    if (!projectId || !bucketId || !amount) {
      return;
    }

    await create.mutateAsync({
      projectId,
      sourceEntryId: String(item.id),
      bucketId,
      amountFiat: amount,
      direction,
      feeFiat: fee || null,
      feeNote: feeNote || null,
      date: date.format('YYYY-MM-DD HH:mm:ss')
    });

    refetch();
    handleClose();
  }, [amount, bucketId, create, date, direction, fee, feeNote, handleClose, item.id, projectId, refetch]);

  const handleRemove = useCallback(async () => {
    await removeLinks.mutateAsync(String(item.id));
    refetch();
    handleClose();
  }, [handleClose, item.id, refetch, removeLinks]);

  return (
    <>
      <Button
        icon={<PiggyBank className={`h-4 w-4 ${linked ? 'text-primary' : ''}`} />}
        onClick={handleOpen}
        size={'small'}
      />

      <Drawer
        buttons={[
          <Button
            disabled={!projectId || !bucketId || !amount}
            key={'apply'}
            loading={create.isLoading}
            onClick={handleApply}
            type={'primary'}
          >
            {'Associar'}
          </Button>
        ]}
        isLoading={create.isLoading || removeLinks.isLoading}
        isOpen={isOpen}
        maskClosable={!create.isLoading && !removeLinks.isLoading}
        onClose={handleClose}
        title={'Associar a pool'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {linked && (
            <div className={'rounded-lg border border-border p-3'}>
              <div className={'mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
                {'Já associado a'}
              </div>

              <div className={'flex flex-col gap-1'}>
                {links.map(link => (
                  <div
                    className={'flex items-center justify-between gap-3 text-sm'}
                    key={link.projectId + link.category}
                  >
                    <span className={'flex min-w-0 items-center gap-1.5'}>
                      <PiggyBank className={'h-3.5 w-3.5 shrink-0 text-primary'} />

                      <span className={'truncate font-medium text-foreground'}>{link.projectName}</span>

                      {link.category && (
                        <span className={'truncate text-muted-foreground'}>
                          {'· '}
                          {link.category}
                        </span>
                      )}
                    </span>

                    <span className={'shrink-0 tabular-nums text-muted-foreground'}>
                      {link.direction === 'out' ? '−' : '+'}
                      {'€'}
                      {fmtMoney(link.amountFiat)}
                      {link.feeFiat ? ` (+€${fmtMoney(link.feeFiat)} ${link.feeNote || 'taxa'})` : ''}
                    </span>
                  </div>
                ))}
              </div>

              <Button className={'mt-2'} danger loading={removeLinks.isLoading} onClick={handleRemove} size={'small'}>
                <Trash2 className={'mr-1 h-3.5 w-3.5'} />
                {'Remover associação'}
              </Button>
            </div>
          )}

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Pool'}</div>

            <Select
              onValueChange={value => {
                setProjectId(value);
                setBucketId('');
              }}
              value={projectId || undefined}
            >
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Selecionar pool'} />
              </SelectTrigger>

              <SelectContent>
                {ownedProjects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Categoria'}</div>

            <Select disabled={!projectId} onValueChange={setBucketId} value={bucketId || undefined}>
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Selecionar categoria'} />
              </SelectTrigger>

              <SelectContent>
                {categoryOptions.map(bucket => (
                  <SelectItem key={bucket.id} value={bucket.id}>
                    {bucket.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={'flex gap-4'}>
            <div className={'flex-1'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Tipo'}</div>

              <Select onValueChange={value => setDirection(value as 'in' | 'out')} value={direction}>
                <SelectTrigger className={'w-full'}>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={'in'}>{'Entrada'}</SelectItem>

                  <SelectItem value={'out'}>{'Saída'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className={'flex-1'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Data'}</div>

              <Input
                onChange={event => event.target.value && setDate(moment(event.target.value, 'YYYY-MM-DD'))}
                type={'date'}
                value={date ? date.format('YYYY-MM-DD') : ''}
              />
            </div>
          </div>

          <div>
            <div className={'mb-1 text-xs text-muted-foreground'}>{'Valor (€)'}</div>

            <Input onChange={event => setAmount(event.target.value)} type={'number'} value={amount} />
          </div>

          <div className={'flex gap-4'}>
            <div className={'flex-1'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Custos acessórios (€)'}</div>

              <Input onChange={event => setFee(event.target.value)} type={'number'} value={fee} />
            </div>

            <div className={'flex-[2]'}>
              <div className={'mb-1 text-xs text-muted-foreground'}>{'Descrição dos custos'}</div>

              <Input
                onChange={event => setFeeNote(event.target.value)}
                placeholder={'ex.: taxa de conversão'}
                value={feeNote}
              />
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}
